import {
  groupBaasInstancesByProgram,
  isBaasHmlInstance,
} from "../../../../Services/baasInstancesApi";
import {
  hasBaasBankingRails,
  isBaasCardsOnlyInstance,
} from "../baasCapabilities";
import {
  BAAS_API_PATH,
  BAAS_CARTOES_PATH,
  BAAS_STABLES_WALLET_PATH,
} from "../constants";

/** Same key as the Settings checklist so progress is shared (MVP localStorage). */
export const homologStorageKey = (instanceId) => `baas.hmlChecklist.${instanceId}`;

const productEnabled = (capabilities, productId) =>
  capabilities?.products?.[productId]?.enabled === true;

/**
 * Homologation wizard steps (HML only). `required` gates re-enable (Ativar HML)
 * after Desativar, and informs Aprovar produção (is_production) — separate from Active.
 * `when(capabilities)` filters by enabled products — omit when = always show (core).
 * `detect` = probe key for auto-complete (null = manual-only / opaque).
 * Cards-only emissor: owner/KYB + shells + API + cards (no Pix/stables/PF-PJ smoke).
 */

/** Issuer / cards product on (with or without banking rails). */
export const isBaasIssuerProductEnabled = (capabilities) =>
  productEnabled(capabilities, "cards") ||
  productEnabled(capabilities, "issuer");

export const HOMOLOG_STEPS = [
  {
    id: "itemOwnerEntity",
    required: true,
    href: "/baas/entidades",
    detect: "ownerEntity",
  },
  {
    id: "itemApproveEntity",
    required: true,
    href: "/baas/entidades",
    detect: "ownerApproved",
  },
  {
    id: "itemEntityPerson",
    required: false,
    href: "/baas/entidades/nova/pessoal",
    /** Banking onboarding smoke — hide on cards-only emissor. */
    when: (capabilities) => !isBaasCardsOnlyInstance(capabilities),
    detect: "clientPf",
  },
  {
    id: "itemEntityBusiness",
    required: false,
    href: "/baas/entidades/nova",
    when: (capabilities) => !isBaasCardsOnlyInstance(capabilities),
    detect: "clientPj",
  },
  {
    id: "itemAccount",
    required: true,
    href: "/baas/contas?create=1",
    /** Conta Digital for banking; Root/operating shells for cards-only emissor. */
    when: (capabilities) =>
      hasBaasBankingRails(capabilities) || isBaasCardsOnlyInstance(capabilities),
    detect: "account",
  },
  {
    id: "itemPixKey",
    required: false,
    href: "/baas/contas",
    when: (capabilities) =>
      !isBaasCardsOnlyInstance(capabilities) &&
      productEnabled(capabilities, "pix"),
    detect: "pixKey",
  },
  {
    id: "itemStables",
    required: false,
    href: BAAS_STABLES_WALLET_PATH,
    when: (capabilities) =>
      !isBaasCardsOnlyInstance(capabilities) &&
      productEnabled(capabilities, "stables"),
    detect: "stables",
  },
  {
    id: "itemFiat",
    required: false,
    href: "/baas/contas",
    when: (capabilities) =>
      !isBaasCardsOnlyInstance(capabilities) &&
      productEnabled(capabilities, "fiat"),
    detect: null,
  },
  {
    id: "itemEfx",
    required: false,
    href: "/baas/contas",
    when: (capabilities) =>
      !isBaasCardsOnlyInstance(capabilities) &&
      productEnabled(capabilities, "efx"),
    detect: null,
  },
  {
    id: "itemApiCall",
    required: true,
    href: BAAS_API_PATH,
    when: (capabilities) => capabilities?.products?.api?.enabled !== false,
    detect: "apiCall",
  },
  {
    id: "itemCards",
    required: false,
    href: BAAS_CARTOES_PATH,
    when: (capabilities) => isBaasIssuerProductEnabled(capabilities),
    detect: "cards",
  },
];

/**
 * Steps visible for the instance's enabled products.
 * Cards-only emissor: owner/KYB + shells + API + cards — no banking smoke steps.
 * Cards step becomes required when the instance is cards-only.
 */
export const getHomologStepsForCapabilities = (capabilities) => {
  const cardsOnly = isBaasCardsOnlyInstance(capabilities);
  return HOMOLOG_STEPS.filter((step) => {
    if (typeof step.when !== "function") return true;
    return step.when(capabilities);
  }).map((step) =>
    cardsOnly && step.id === "itemCards"
      ? { ...step, required: true }
      : step,
  );
};

/** @deprecated Prefer getHomologStepsForCapabilities — kept for callers without caps. */
export const CHECKLIST_ITEMS = HOMOLOG_STEPS.map((step) => step.id);

export const HOMOLOG_REQUIRED_IDS = HOMOLOG_STEPS.filter((s) => s.required).map(
  (s) => s.id,
);

const emptyHomologState = () => ({ checks: {}, sources: {} });

/**
 * Load checklist state. Legacy flat `{ itemId: true }` migrates to
 * `{ checks, sources }` with source `manual`.
 */
export const loadHomologState = (instanceId) => {
  if (!instanceId) return emptyHomologState();
  try {
    const raw = localStorage.getItem(homologStorageKey(instanceId));
    if (!raw) return emptyHomologState();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyHomologState();

    if (parsed.v === 2 && parsed.checks && typeof parsed.checks === "object") {
      return {
        checks: { ...parsed.checks },
        sources:
          parsed.sources && typeof parsed.sources === "object"
            ? { ...parsed.sources }
            : {},
      };
    }

    const checks = {};
    const sources = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (key === "v" || key === "checks" || key === "sources") continue;
      checks[key] = Boolean(value);
      if (value) sources[key] = "manual";
    }
    return { checks, sources };
  } catch {
    return emptyHomologState();
  }
};

export const saveHomologState = (instanceId, { checks = {}, sources = {} } = {}) => {
  if (!instanceId) return;
  try {
    localStorage.setItem(
      homologStorageKey(instanceId),
      JSON.stringify({ v: 2, checks, sources }),
    );
  } catch {
    // ignore quota / private mode
  }
};

/** @deprecated Prefer loadHomologState — returns flat checks only. */
export const loadHomologChecks = (instanceId) => loadHomologState(instanceId).checks;

/** @deprecated Prefer saveHomologState — preserves existing sources. */
export const saveHomologChecks = (instanceId, checks) => {
  const prev = loadHomologState(instanceId);
  saveHomologState(instanceId, { checks, sources: prev.sources });
};

export const getRequiredHomologIds = (steps = HOMOLOG_STEPS) =>
  steps.filter((s) => s.required).map((s) => s.id);

export const areRequiredHomologStepsDone = (checks, steps = HOMOLOG_STEPS) =>
  getRequiredHomologIds(steps).every((id) => Boolean(checks?.[id]));

export const countHomologProgress = (checks, steps = HOMOLOG_STEPS) => {
  const done = steps.filter((s) => Boolean(checks?.[s.id])).length;
  return { done, total: steps.length };
};

/**
 * Merge auto-detected completions into state.
 * Auto-checked steps that are no longer detected are cleared
 * (does not override a manual check or a manual uncheck).
 */
export const mergeHomologAutoDetections = (state, detected = {}, steps = []) => {
  const checks = { ...(state?.checks || {}) };
  const sources = { ...(state?.sources || {}) };
  let changed = false;

  const detectIds = (Array.isArray(steps) ? steps : [])
    .filter((step) => step?.id && step.detect)
    .map((step) => step.id);

  for (const [id, isDone] of Object.entries(detected)) {
    if (!isDone) continue;
    if (sources[id] === "manual" && checks[id] === false) continue;

    if (!checks[id]) {
      checks[id] = true;
      sources[id] = "auto";
      changed = true;
    } else if (sources[id] !== "manual" && sources[id] !== "auto") {
      sources[id] = "auto";
      changed = true;
    }
  }

  for (const id of detectIds) {
    if (sources[id] === "manual") continue;
    if (checks[id] && !detected[id]) {
      delete checks[id];
      delete sources[id];
      changed = true;
    }
  }

  return { checks, sources, changed };
};

/** Manual checkbox toggle — always marks source as `manual`. */
export const toggleHomologManualCheck = (state, id) => {
  const checks = { ...(state?.checks || {}) };
  const sources = { ...(state?.sources || {}) };
  checks[id] = !checks[id];
  sources[id] = "manual";
  return { checks, sources };
};

export const getHomologStepSource = (sources, id) => {
  const raw = sources?.[id];
  if (raw === "auto" || raw === "manual") return raw;
  return null;
};

/**
 * Resolve Prod + HML pair for the selected console instance.
 */
export const resolveBaasHomologPair = (instances, selectedInstance) => {
  if (!selectedInstance) {
    return { production: null, development: null, group: null };
  }

  const groups = groupBaasInstancesByProgram(instances || []);
  const group =
    groups.find(
      (g) =>
        g.production?.id === selectedInstance.id ||
        g.development?.id === selectedInstance.id,
    ) || null;

  if (group) {
    return {
      production: group.production || null,
      development: group.development || null,
      group,
    };
  }

  if (isBaasHmlInstance(selectedInstance)) {
    return { production: null, development: selectedInstance, group: null };
  }

  return { production: selectedInstance, development: null, group: null };
};

/**
 * Pair still needs homologation work (HML pending, Prod not liberated, or twin missing).
 * Wizard steps always run against the HML twin; Prod liberation is a separate manual Valut action.
 */
export const pairNeedsBaasHomologacao = (selectedInstance, instances = []) => {
  if (!selectedInstance?.baasInstanceId) return false;

  const { production, development } = resolveBaasHomologPair(
    instances,
    selectedInstance,
  );
  const prod =
    production ||
    (!isBaasHmlInstance(selectedInstance) ? selectedInstance : null);
  const hml =
    development ||
    (isBaasHmlInstance(selectedInstance) ? selectedInstance : null);

  if (hml && hml.active === false) return true;
  if (prod && prod.isProduction !== true) return true;
  if (prod && !(Number(prod.hmlInstanceId) > 0) && !hml) return true;

  return false;
};

/**
 * Show Homologação nav primarily on HML, or on Prod when the linked twin still needs work
 * (Prod lane shows an awaiting link — not the step checklist).
 */
export const needsBaasHomologacao = (selectedInstance, instances = []) =>
  pairNeedsBaasHomologacao(selectedInstance, instances);

/**
 * Full checklist/wizard is HML-only; Prod uses the awaiting / approve-prod gate.
 * Prefer the resolved development twin id so a mis-tagged Prod card never shows steps.
 */
export const shouldShowHomologWizardSteps = (
  selectedInstance,
  instances = [],
) => {
  if (!selectedInstance) return false;

  const env = String(selectedInstance.environment ?? "").toLowerCase();
  if (env === "production" || env === "prod") return false;

  const { development } = resolveBaasHomologPair(instances, selectedInstance);
  if (development?.id) {
    return selectedInstance.id === development.id;
  }

  return isBaasHmlInstance(selectedInstance);
};
